import React, { useRef, useState, useEffect } from 'react';
import Uppy from '@uppy/core';
import AwsS3Multipart from '@uppy/aws-s3-multipart';
import { KitDocumentModel } from '../submission_kits/components/KitModels';
import Button from '../submission_boards/component/Button';
import kitDocumentService from '../services/KitDocumentService';
import { Play, Pause, Camera } from 'lucide-react';
import { showToast } from './Toast';

interface VideoThumbnailSelectorProps {
  kitDocumentModel: KitDocumentModel;
  record_uuid: string;
  refreshMediaHandler: () => void;
  dialogCloseHandler: () => void;
}

export default function VideoThumbnailSelector({
  kitDocumentModel,
  record_uuid,
  refreshMediaHandler,
  dialogCloseHandler,
}: VideoThumbnailSelectorProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const uppy = useRef<Uppy | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    // Initialize Uppy for uploading the captured frame
    uppy.current = new Uppy({
      debug: false,
      autoProceed: false,
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['image/*'],
      },
    });

    uppy.current.use(AwsS3Multipart, {
      companionUrl: '/',
    });

    uppy.current.on('upload-success', async (file, response) => {
      try {
        const category_id = kitDocumentModel.category_id as any;
        const mediakit_uuid = record_uuid;
        const kit_document_uuid = kitDocumentModel.uuid;

        // replace_crop: 2 means update cropped_file (the thumbnail)
        await kitDocumentService.updateKitDocument(
          file,
          category_id,
          mediakit_uuid,
          kit_document_uuid,
          2
        );
        showToast('Video thumbnail updated successfully', 'success');
        refreshMediaHandler();
        dialogCloseHandler();
      } catch (error) {
        console.error('Error updating thumbnail:', error);
        showToast('Failed to update video thumbnail', 'danger');
      } finally {
        setIsSaving(false);
      }
    });

    return () => {
      if (uppy.current) {
        uppy.current.close();
      }
    };
  }, [kitDocumentModel, record_uuid, refreshMediaHandler, dialogCloseHandler]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const captureFrame = () => {
    if (videoRef.current && canvasRef.current && uppy.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(async (blob) => {
          if (blob && uppy.current) {
            setIsSaving(true);
            const fileName = `thumbnail_${kitDocumentModel.file_name.split('.')[0]}.png`;
            const file = new File([blob], fileName, { type: 'image/png' });

            uppy.current.cancelAll();
            uppy.current.addFile({
              name: file.name,
              type: file.type,
              data: file,
            });

            try {
              await uppy.current.upload();
            } catch (error) {
              console.error('Upload failed:', error);
              setIsSaving(false);
            }
          }
        }, 'image/png');
      }
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow-lg">
      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden group">
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          src={kitDocumentModel.file_url}
          className="w-full h-full object-contain"
          onLoadedMetadata={() => setVideoLoaded(true)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          controls
        />
        {!videoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            Loading video...
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-2">
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={togglePlay}
            disabled={!videoLoaded || isSaving}
            className="flex items-center gap-2"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row  gap-2">
          <Button
            variant="secondary"
            onClick={dialogCloseHandler}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={captureFrame}
            disabled={!videoLoaded || isSaving}
            className="flex items-center gap-2"
          >
            <Camera className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Set Frame as Thumbnail'}
          </Button>
        </div>
      </div>

      <div className="text-xs text-gray-500 italic">
        Tip: Pause the video at the frame you want to use as a thumbnail, then click "Set Frame as Thumbnail".
      </div>
    </div>
  );
}
